import React, { useState, useEffect, useCallback } from "react";
import { AlertCircle, Cloud, Database, Download, HardDrive, PlayCircle, RefreshCw, ShieldAlert } from "lucide-react";
import { fetchBackupJobs, triggerBackup, downloadBackupFile } from "./backupApi";
import type { BackupJob } from "./types";

const statusStyle: Record<BackupJob["status"], string> = {
  Completed: "bg-emerald-950 text-emerald-300 border-emerald-800",
  Running:   "bg-indigo-950 text-indigo-300 border-indigo-800 animate-pulse",
  Failed:    "bg-rose-950 text-rose-300 border-rose-800",
};

const typeStyle: Record<BackupJob["type"], string> = {
  "Full":        "bg-amber-950 text-amber-300 border-amber-800",
  "Incremental": "bg-cyan-950 text-cyan-300 border-cyan-800",
  "Schema-Only": "bg-purple-950 text-purple-300 border-purple-800",
};

export const BackupCenterPage: React.FC = () => {
  const [jobs, setJobs] = useState<BackupJob[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const realJobs = await fetchBackupJobs();
      setJobs(realJobs);
    } catch (err: any) {
      console.error("Backup jobs API error:", err);
      setError(err?.message || "Failed to load database backup jobs from system API.");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Polling while any job is in "Running" status
  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status === "Running");
    if (!hasRunning) return;

    const interval = setInterval(() => {
      loadJobs();
    }, 4000);

    return () => clearInterval(interval);
  }, [jobs, loadJobs]);

  const handleTrigger = async (type: "Full" | "Incremental" | "Schema-Only") => {
    setTriggering(type);
    setFeedback(null);
    const res = await triggerBackup(type);
    setTriggering(null);

    if (res.success) {
      setFeedback({
        message: `${res.message} Status will automatically update once backup execution completes.`,
        type: "success",
      });
      await loadJobs();
    } else {
      setFeedback({ message: res.message, type: "error" });
    }
  };

  const lastCompletedFull = jobs.find((j) => j.type === "Full" && j.status === "Completed") || jobs.find((j) => j.status === "Completed");

  const isR2Job = (job: BackupJob) => {
    const provider = (job.storage_provider || "").toLowerCase();
    const loc = (job.location || "").toLowerCase();
    return provider.includes("r2") || provider.includes("cloudflare") || loc.startsWith("backups/") || loc.startsWith("r2://") || loc.startsWith("s3://");
  };

  return (
    <div className="space-y-4 text-xs font-sans">
      {/* Toast Feedback */}
      {feedback && (
        <div
          className={`p-3 rounded-xl border text-xs font-medium ${
            feedback.type === "success"
              ? "bg-emerald-950/90 text-emerald-300 border-emerald-800"
              : "bg-rose-950/90 text-rose-300 border-rose-800"
          }`}
        >
          {feedback.message}
        </div>
      )}

      {/* Actions Row */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-slate-900/80 border border-slate-800 rounded-xl">
        <button
          onClick={() => handleTrigger("Full")}
          disabled={triggering !== null}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl text-[11px] transition-colors disabled:opacity-50"
        >
          <PlayCircle className={`w-4 h-4 ${triggering === "Full" ? "animate-spin" : ""}`} />
          {triggering === "Full" ? "Triggering..." : "Manual Full Backup"}
        </button>
        <button
          onClick={() => handleTrigger("Incremental")}
          disabled={triggering !== null}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl text-[11px] transition-colors disabled:opacity-50"
        >
          <PlayCircle className={`w-4 h-4 ${triggering === "Incremental" ? "animate-spin" : ""}`} />
          {triggering === "Incremental" ? "Triggering..." : "Incremental Now"}
        </button>
        <button
          onClick={loadJobs}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl text-[11px] transition-colors disabled:opacity-50 ml-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh History
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Daily Full Backup", value: "03:00 IST", sub: "Retention: 30 days" },
          { label: "Incremental Cadence", value: "Every 6h", sub: "Retention: 7 days" },
          {
            label: "Last Completed Backup",
            value: lastCompletedFull?.size || "—",
            sub: lastCompletedFull ? `${lastCompletedFull.type} · ${lastCompletedFull.duration}` : "No completed backups",
          },
          { label: "RPO Target", value: "< 6 Hours", sub: "Current RTO: ~45m" },
        ].map((c) => (
          <div key={c.label} className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
            <p className="text-[10px] text-slate-500 font-semibold">{c.label}</p>
            <p className="text-base font-bold font-mono text-slate-100 mt-0.5">{c.value}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Retention Policy */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-3">
        <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase">Retention Policy</h3>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Full Backups",         value: "30 days" },
            { label: "Incremental Backups",  value: "7 days"  },
            { label: "Schema-Only Backups",  value: "90 days" },
          ].map((r) => (
            <div key={r.label} className="flex items-center justify-between p-2.5 bg-slate-950 border border-slate-800 rounded-lg">
              <span className="text-[10px] text-slate-400">{r.label}</span>
              <span className="text-[10px] font-bold font-mono text-indigo-300">{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* API Error State */}
      {error && (
        <div className="p-4 bg-rose-950/80 border border-rose-800 rounded-xl flex items-start gap-3 text-rose-200">
          <AlertCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-rose-300">API Connection Failure</p>
            <p className="text-xs text-rose-400/90 mt-1">{error}</p>
            <button
              onClick={loadJobs}
              className="mt-3 px-3 py-1.5 bg-rose-800 hover:bg-rose-700 text-white font-bold rounded-lg text-[11px] transition-colors"
            >
              Retry API Fetch
            </button>
          </div>
        </div>
      )}

      {/* Loading Skeleton */}
      {loading && jobs.length === 0 && !error && (
        <div className="p-8 bg-slate-900/80 border border-slate-800 rounded-xl text-center space-y-3">
          <RefreshCw className="w-6 h-6 text-indigo-400 animate-spin mx-auto" />
          <p className="text-slate-400 font-medium">Fetching real backup job history from database...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && jobs.length === 0 && (
        <div className="p-8 text-center bg-slate-950 border border-slate-800 rounded-xl space-y-3">
          <Database className="w-10 h-10 text-slate-600 mx-auto" />
          <div>
            <p className="text-slate-200 font-bold text-sm">No Database Backups Recorded</p>
            <p className="text-slate-500 text-xs mt-1">No backup jobs have been recorded in the database yet.</p>
          </div>
          <button
            onClick={() => handleTrigger("Full")}
            disabled={triggering !== null}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl text-xs transition-colors"
          >
            Trigger First Manual Backup
          </button>
        </div>
      )}

      {/* Backup History Table */}
      {!error && jobs.length > 0 && (
        <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase">Backup History</h3>
            <span className="text-[10px] text-slate-500 font-mono">{jobs.length} recorded job(s)</span>
          </div>

          <div className="space-y-2">
            {jobs.map((job) => {
              const r2Offsite = isR2Job(job);
              return (
                <div
                  key={job.id}
                  className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl hover:border-slate-700 transition-colors space-y-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase font-mono shrink-0 ${
                        typeStyle[job.type] || typeStyle["Full"]
                      }`}
                    >
                      {job.type}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0 font-medium">{job.trigger}</span>

                    {/* Storage Provider & R2 Indicator */}
                    {r2Offsite ? (
                      <span className="flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 bg-sky-950 text-sky-300 border border-sky-800 rounded uppercase font-mono shrink-0">
                        <Cloud className="w-3 h-3 text-sky-400" />
                        Cloudflare R2 (Offsite)
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 bg-slate-800 text-slate-300 border border-slate-700 rounded uppercase font-mono shrink-0">
                        <HardDrive className="w-3 h-3 text-slate-400" />
                        Local Storage
                      </span>
                    )}

                    {job.tenant_schema && (
                      <span className="text-[9px] font-mono text-slate-500 px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded shrink-0">
                        scope: {job.tenant_schema}
                      </span>
                    )}

                    <span className="text-[11px] font-bold text-slate-200 flex-1 font-mono truncate ml-auto text-right">
                      {job.completedAt}
                    </span>

                    <span className="text-[10px] text-slate-400 font-mono shrink-0">{job.size}</span>
                    <span className="text-[10px] text-slate-400 font-mono shrink-0">{job.duration}</span>

                    <span
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 ${
                        statusStyle[job.status] || statusStyle["Completed"]
                      }`}
                    >
                      {job.status}
                    </span>

                    {job.status === "Completed" && (
                      <button
                        onClick={() => downloadBackupFile(job.id, job.file_name || `${job.id}.dump`)}
                        className="p-1.5 text-slate-400 hover:text-indigo-300 hover:bg-slate-900 rounded-lg transition-colors"
                        title="Download backup file archive"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Location & Object Key */}
                  <div className="flex flex-wrap items-center justify-between text-[10px] font-mono text-slate-400 pt-1.5 border-t border-slate-900 gap-2">
                    <span className="truncate max-w-md">
                      <strong className="text-slate-500">Location:</strong> {job.location || "—"}
                    </span>
                    {job.file_name && (
                      <span className="text-slate-500 truncate">
                        File: {job.file_name}
                      </span>
                    )}
                  </div>

                  {/* Error State Display */}
                  {job.status === "Failed" && (
                    <div className="p-2.5 bg-rose-950/60 border border-rose-800/80 rounded-lg flex items-start gap-2 text-rose-300 text-[11px] font-mono">
                      <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-rose-200">Execution Error</p>
                        <p className="text-rose-400 mt-0.5">{job.error_message || "Backup process terminated unexpectedly."}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
